/**
 * CFS product page (build spec §6).
 * - Gallery: swipeable track of all media (images and videos) with arrows, thumbnails and a counter.
 * - Quantity stepper and variant select (updates the price on the Add button).
 * - Add to basket adds the chosen quantity on top of what is already in the basket, through
 *   assets/cfs-cart.js (fee-free state, Horizon cart events, drawer re-render).
 * - Sticky "Add to basket · £x" bar on mobile while the main button is off screen.
 * - Bundler multibuy: − / + buttons around its bundle quantity field (owner request).
 * - Pre-order variants (tracked, "continue selling"): "Available to pre-order" at zero stock, and a calm note
 *   with "Order N only" when the quantity (plus what is already in the basket) is more than the stock.
 * - Video deep link: #video-gallery opens the first product video in a dialog (email campaigns).
 */
import { setVariantQuantity, lineForVariant, CART_STATE_EVENT } from '@theme/cfs-cart';

const root = document.querySelector('.cfs-product');
const form = /** @type {HTMLFormElement | null} */ (document.querySelector('[data-cfs-product-form]'));

// Gallery: a scroll-snap track (swipe on touch); arrows, thumbnails and the counter follow it.
const gallery = document.querySelector('[data-cfs-gallery]');
const track = /** @type {HTMLElement | null} */ (gallery?.querySelector('[data-cfs-track]') ?? null);
if (gallery && track) {
  const slides = /** @type {HTMLElement[]} */ ([...track.querySelectorAll('[data-cfs-slide]')]);
  const thumbs = /** @type {HTMLElement[]} */ ([...gallery.querySelectorAll('[data-cfs-thumb]')]);
  const prev = /** @type {HTMLButtonElement | null} */ (gallery.querySelector('[data-cfs-gallery-prev]'));
  const next = /** @type {HTMLButtonElement | null} */ (gallery.querySelector('[data-cfs-gallery-next]'));
  const counter = gallery.querySelector('[data-cfs-gallery-counter]');
  let current = 0;

  const show = (/** @type {number} */ index, smooth = true) => {
    const target = slides[Math.max(0, Math.min(slides.length - 1, index))];
    if (!target) return;
    track.scrollTo({ left: target.offsetLeft, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
  };

  const update = () => {
    const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    if (index === current) return;
    // Pause a video that has been swiped away.
    slides[current]?.querySelector('video')?.pause();
    current = index;
    thumbs.forEach((thumb, i) => thumb.toggleAttribute('aria-current', i === current));
    thumbs[current]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (prev) prev.disabled = current === 0;
    if (next) next.disabled = current === slides.length - 1;
    if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
  };

  let frame = 0;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(update);
  }, { passive: true });
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
  prev?.addEventListener('click', () => show(current - 1));
  next?.addEventListener('click', () => show(current + 1));
  // Video slides: a click anywhere on the slide plays/pauses (the native controls still work).
  for (const slide of slides) {
    const video = slide.querySelector('video');
    if (!video) continue;
    video.querySelector('img')?.setAttribute('alt', ''); // Shopify's no-video fallback image has no alt text
    // Phones draw their own play icon over a paused video with controls, next to ours (owner): the
    // browser's controls appear once it plays (seek, sound, full screen). Without JS they stay on.
    video.controls = false;
    video.addEventListener('play', () => {
      video.controls = true;
      slide.classList.add('is-playing', 'has-played');
    });
    video.addEventListener('pause', () => slide.classList.remove('is-playing'));
    slide.addEventListener('click', (event) => {
      // Once its controls are showing, clicks on the video itself are the browser's.
      if (event.target === video && video.controls) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
  }

  // Video deep link (email campaigns): /products/<handle>#video-gallery (also #video, #video-2)
  // opens the product's video in a dialog, ready to play, as the previous theme's popup did.
  // The dialog is built from the gallery's own video slide, and only when the hash asks for it,
  // so nothing loads otherwise. No autoplay. Closing it clears the hash and leaves the gallery on
  // the first image, so the shopper lands on a product page they can buy from.
  const videoSlides = slides.filter((slide) => slide.hasAttribute('data-cfs-video'));
  if (videoSlides.length) {
    const VIDEO_HASH = /^#video(?:-gallery)?(?:-(\d+))?$/i;
    /** @type {HTMLDialogElement | null} */
    let dialog = null;

    const openVideo = (/** @type {HTMLElement} */ slide) => {
      const source = slide.querySelector('video, iframe');
      if (!source || dialog) return;

      // Only matters when the product's first media is the video: show an image behind the dialog.
      const firstImage = slides.findIndex((s) => s.classList.contains('cfs-product__slide--image'));
      if (firstImage >= 0 && !slides[current]?.classList.contains('cfs-product__slide--image')) show(firstImage, false);

      if (source instanceof HTMLVideoElement) source.pause(); // the gallery's copy, if it was playing

      const media = /** @type {HTMLVideoElement | HTMLIFrameElement} */ (source.cloneNode(true));
      media.className = 'cfs-video-modal__media';
      media.removeAttribute('loading');
      if (media instanceof HTMLVideoElement) {
        media.preload = 'metadata'; // duration + poster, not the whole file
        media.querySelector('img')?.remove(); // Shopify's no-video fallback image has no alt text
      }

      const frame = document.createElement('div');
      frame.className = 'cfs-video-modal__frame';
      frame.style.setProperty('--cfs-video-ratio', String(Number(slide.dataset.cfsRatio) || 16 / 9));
      const player = document.createElement('div');
      player.className = 'cfs-video-modal__player';
      player.append(media);
      frame.append(player);

      // The gallery slide's own big play button, so the two look and read the same.
      const big = /** @type {HTMLButtonElement | null} */ (slide.querySelector('[data-cfs-bigplay]')?.cloneNode(true) ?? null);
      if (big && media instanceof HTMLVideoElement) {
        big.addEventListener('click', () => media.play().catch(() => {}));
        media.controls = false; // as in the gallery: one play button until it plays
        media.addEventListener('click', () => {
          if (!media.controls) media.play().catch(() => {});
        });
        media.addEventListener('play', () => {
          media.controls = true;
          player.classList.add('is-playing', 'has-played');
        });
        media.addEventListener('pause', () => player.classList.remove('is-playing'));
        player.append(big);
      }

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'cfs-video-modal__close';
      closeButton.setAttribute('aria-label', 'Close video');
      closeButton.innerHTML = '<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

      const modal = document.createElement('dialog');
      dialog = modal;
      modal.className = 'cfs-video-modal';
      modal.setAttribute('aria-label', `Product video: ${source.getAttribute('aria-label') || source.getAttribute('title') || document.title}`);
      frame.append(closeButton);
      modal.append(frame);
      closeButton.addEventListener('click', () => modal.close());
      // A click on the dimmed area (the dialog itself, outside the player) closes it too.
      modal.addEventListener('click', (event) => {
        if (event.target === modal) modal.close();
      });
      // Esc, the close button, the dimmed area and Back (hashchange) all end here.
      modal.addEventListener('close', () => {
        if (media instanceof HTMLVideoElement) {
          media.pause();
          media.replaceChildren(); // drop the <source> ...
          media.load(); // ... and abort the download
        }
        modal.remove();
        dialog = null;
        document.documentElement.classList.remove('cfs-video-open');
        if (VIDEO_HASH.test(location.hash)) history.replaceState(history.state, '', location.pathname + location.search);
        // Back to where the video lives: its thumbnail, or the gallery on phones (thumbnails hidden).
        const thumb = thumbs[slides.indexOf(slide)];
        (thumb?.offsetParent ? thumb : track).focus({ preventScroll: true });
      });

      document.body.append(modal);
      document.documentElement.classList.add('cfs-video-open');
      modal.showModal();
      (big ?? closeButton).focus({ preventScroll: true });
    };

    const openFromHash = () => {
      const match = VIDEO_HASH.exec(location.hash);
      if (!match) {
        dialog?.close();
        return;
      }
      const slide = videoSlides[Number(match[1] ?? 1) - 1] ?? videoSlides[0];
      if (slide) openVideo(slide);
    };
    window.addEventListener('hashchange', openFromHash);
    openFromHash();
  }

  track.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') show(current + 1);
    if (event.key === 'ArrowLeft') show(current - 1);
  });
}

if (form && root) {
  const qty = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cfs-qty]'));
  const variantInput = /** @type {HTMLInputElement | HTMLSelectElement} */ (form.querySelector('[data-cfs-variant-input]'));
  const message = /** @type {HTMLElement | null} */ (form.querySelector('[data-cfs-product-message]'));
  // Errors ("Only 7 available") get their own box above the basket line, which stays (owner)
  const errorBox = document.createElement('p');
  errorBox.className = 'cfs-product__message';
  errorBox.dataset.error = 'true';
  errorBox.setAttribute('role', 'alert');
  message?.before(errorBox);
  const clearError = () => {
    if (errorBox.textContent) errorBox.textContent = '';
  };
  const addButtons = /** @type {HTMLButtonElement[]} */ ([...document.querySelectorAll('[data-cfs-add-button], [data-cfs-sticky-button]')]);
  const prices = [...document.querySelectorAll('[data-cfs-add-price]')];
  const mainPrice = document.querySelector('[data-cfs-price]');
  const stickyPrice = document.querySelector('[data-cfs-sticky-price]');

  const quantity = () => Math.max(1, Math.min(99, Number(qty?.value) || 1));

  /** The element that carries the chosen variant's data attributes (the select's option, or the hidden input). */
  const variantSource = () => (variantInput instanceof HTMLSelectElement ? variantInput.selectedOptions[0] : variantInput);

  /** Unit price (pence) of the chosen variant. */
  const unitCents = () => Number(variantSource()?.dataset.priceCents) || 0;
  const format = (/** @type {number} */ cents) => {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (form.dataset.moneyFormat || '£{{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  };

  // Pre-order variants (tracked, "continue selling"; Liquid sets data-preorder and data-stock on them).
  // When what is already in the basket plus the chosen quantity is more than the stock on hand, a calm
  // note says how many are in stock and how many are on pre-order, and offers "Order N only". Updates as
  // the shopper types, presses − / + or changes the variant. Normal variants never show any of it.
  const preorderBox = /** @type {HTMLElement | null} */ (form.querySelector('[data-cfs-preorder]'));
  const preorderHead = preorderBox?.querySelector('[data-cfs-preorder-head]') ?? null;
  const preorderCopy = preorderBox?.querySelector('[data-cfs-preorder-copy]') ?? null;
  const preorderOnly = /** @type {HTMLButtonElement | null} */ (preorderBox?.querySelector('[data-cfs-preorder-only]') ?? null);
  const preorderLive = form.querySelector('[data-cfs-preorder-live]');
  const stockLabel = document.querySelector('[data-cfs-stock-label]');
  const stockText = document.querySelector('[data-cfs-stock-text]');
  const preorderIds = [preorderHead?.id, preorderCopy?.id].filter(Boolean).join(' ');
  let liveTimer = 0;

  /** Stock on hand (0 or more) when the chosen variant is a pre-order variant, otherwise -1. */
  const preorderStock = () => {
    const source = variantSource();
    return source?.dataset.preorder === 'true' ? Math.max(0, Number(source.dataset.stock) || 0) : -1;
  };

  /** @param {boolean} announce false for changes the shopper did not just make (page load, drawer, after adding) */
  const renderPreorder = (announce = true) => {
    if (!preorderBox) return;
    const stock = preorderStock();
    const inBasket = lineForVariant(variantInput.value)?.quantity ?? 0;
    const total = inBasket + quantity();
    const over = stock >= 0 ? Math.max(0, total - stock) : 0;
    const room = stock - inBasket; // still to come from stock

    if (stockLabel && stockText && !stockLabel.classList.contains('cfs-product__stock--out')) {
      const empty = stock === 0;
      if (stockLabel.classList.contains('cfs-product__stock--preorder') !== empty) stockLabel.classList.toggle('cfs-product__stock--preorder', empty);
      const label = empty ? 'Available to pre-order' : 'In stock';
      if (stockText.textContent !== label) stockText.textContent = label;
    }

    // Basket at exactly the stock level with quantity 1: nothing is on pre-order, so no note (owner);
    // the basket message below says how many are in the basket.
    const atStock = stock >= 1 && quantity() === 1 && inBasket === stock;
    const showNote = over > 0 && !atStock;

    let head = '';
    if (showNote) {
      head = stock < 1
        ? 'On pre-order'
        : inBasket > 0 && quantity() === 1 // owner: at quantity 1, say what the basket has now
          ? inBasket > stock
            ? `Your basket has ${inBasket} (${stock} in stock + ${inBasket - stock} on pre-order)`
            : ''
          : `${inBasket ? `Your basket would have ${total} (${stock} in stock + ${over} on pre-order)` : `${stock} in stock + ${over} on pre-order`}`;
      if (preorderHead && preorderHead.textContent !== head) preorderHead.textContent = head;
    }
    if (preorderBox.hidden !== !showNote) preorderBox.hidden = !showNote;

    if (preorderOnly) {
      const show = showNote && stock >= 1 && room >= 1;
      if (preorderOnly.hidden !== !show) preorderOnly.hidden = !show;
      if (show) {
        const label = inBasket > 0 ? `Order ${room} more only` : `Order ${room} only`;
        if (preorderOnly.textContent !== label) preorderOnly.textContent = label;
        preorderOnly.dataset.qty = String(room);
      }
    }

    const describedBy = showNote ? preorderIds : '';
    for (const button of addButtons) {
      if ((button.getAttribute('aria-describedby') ?? '') !== describedBy) {
        if (describedBy) button.setAttribute('aria-describedby', describedBy);
        else button.removeAttribute('aria-describedby');
      }
    }

    // The visually hidden live region is written after a pause (typing "12" must not announce "1" then "12");
    // changes the shopper did not just make only clear it.
    window.clearTimeout(liveTimer);
    const live = showNote ? `${head}. ${preorderCopy?.textContent ?? ''}` : '';
    if (announce) {
      liveTimer = window.setTimeout(() => {
        if (preorderLive && preorderLive.textContent !== live) preorderLive.textContent = live;
      }, 300);
    } else if (preorderLive?.textContent) {
      preorderLive.textContent = '';
    }
  };

  preorderOnly?.addEventListener('click', () => {
    if (!qty) return;
    qty.value = String(Math.max(1, Math.min(99, Number(preorderOnly.dataset.qty) || 1)));
    updateTotal();
    // Adds straight away (owner): the button hides with the note, so focus goes to Add, not the field
    // (which would open the phone keyboard).
    form.querySelector('[data-cfs-add-button]')?.focus({ preventScroll: true });
    form.requestSubmit();
  });
  document.addEventListener(CART_STATE_EVENT, () => renderPreorder(false));

  /** "Add to basket · £x" shows the total for the chosen quantity. @param {boolean} announce */
  const updateTotal = (announce = true) => {
    renderPreorder(announce);
    const cents = unitCents();
    if (!cents) return;
    for (const el of prices) el.textContent = format(cents * quantity());
  };
  renderPreorder(false);

  for (const step of form.querySelectorAll('[data-cfs-qty-step]')) {
    step.addEventListener('click', () => {
      if (!qty) return;
      qty.value = String(Math.max(1, Math.min(99, quantity() + Number(/** @type {HTMLElement} */ (step).dataset.cfsQtyStep))));
      updateTotal();
    });
  }
  qty?.addEventListener('input', () => updateTotal());
  qty?.addEventListener('change', () => {
    if (qty) qty.value = String(quantity());
    updateTotal();
  });

  if (variantInput instanceof HTMLSelectElement) {
    variantInput.addEventListener('change', () => {
      const option = variantInput.selectedOptions[0];
      const price = option?.dataset.price ?? '';
      if (mainPrice) mainPrice.textContent = price;
      if (stickyPrice) stickyPrice.textContent = price;
      updateTotal();
      justAdded = null;
      clearError();
      showBasketMessage();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantInput.value);
      history.replaceState(history.state, '', url.toString());
    });
  }

  // "N in your basket · View basket" whenever the chosen variant is in the basket (owner): on load,
  // after any basket change (drawer, other tabs) and on variant change, not only right after an add.
  // While the pre-order note above gives the basket's counts, this just says it's in the basket.
  /** @type {string | null} */
  let justAdded = null;
  const showBasketMessage = () => {
    if (!message) return;
    const variantId = variantInput.value;
    const inBasket = lineForVariant(variantId)?.quantity ?? 0;
    const stock = preorderStock();
    const noteHasCounts = stock >= 0 && inBasket > stock && quantity() === 1; // "Your basket has 5: …"
    const text = inBasket === 0 ? '' : noteHasCounts ? (justAdded === variantId ? 'Added to your basket. ' : 'In your basket. ') : `${inBasket} in your basket. `;
    if (message.dataset.cfsShown === text) return; // unchanged: no DOM write
    message.dataset.cfsShown = text;
    message.innerHTML = '';
    if (!text) return;
    message.append(text);
    const view = document.createElement('button');
    view.type = 'button';
    view.className = 'cfs-link cfs-product__view-basket';
    view.textContent = 'View basket';
    view.addEventListener('click', () => /** @type {any} */ (document.getElementById('cart-drawer'))?.open?.() ?? window.location.assign('/cart'));
    message.append(view);
  };
  showBasketMessage();
  document.addEventListener(CART_STATE_EVENT, showBasketMessage);
  // The quantity decides whether the note gives the counts (typing, or the − / + buttons).
  form.addEventListener('input', showBasketMessage);
  form.addEventListener('click', () => queueMicrotask(showBasketMessage));

  // Clicks are queued: each one reads the basket after the previous add has finished, so two quick
  // clicks add two lots instead of both setting the same total.
  let queue = Promise.resolve();
  let pending = 0;

  /**
   * @param {SubmitEvent | MouseEvent} event
   * @returns {Promise<boolean>} whether this add went through
   */
  const add = (event) => {
    event.preventDefault();
    const variantId = variantInput.value;
    const wanted = quantity();
    pending += 1;
    for (const button of addButtons) button.setAttribute('aria-busy', 'true');
    const done = queue.then(() => addNow(variantId, wanted));
    queue = done.then(() => {});
    return done;
  };

  /**
   * @param {string} variantId
   * @param {number} wanted
   * @returns {Promise<boolean>}
   */
  const addNow = async (variantId, wanted) => {
    const current = lineForVariant(variantId)?.quantity ?? 0;
    clearError();
    try {
      await setVariantQuantity({ variantId, quantity: current + wanted, source: form });
      justAdded = variantId;
      showBasketMessage();
      if (qty) qty.value = '1';
      updateTotal(false);
      return true;
    } catch (error) {
      errorBox.textContent = /** @type {Error} */ (error)?.message || 'Could not add to your basket';
      showBasketMessage(); // what the basket holds now, under the error
      return false;
    } finally {
      pending -= 1;
      if (pending === 0) for (const button of addButtons) button.removeAttribute('aria-busy');
    }
  };

  form.addEventListener('submit', add);
  // Sticky bar Add (owner): adds, says so on the button for a moment ("✓ Added"), then scrolls up to
  // the start of the product image, so the quantity, basket message and any pre-order note show.
  const stickyButton = /** @type {HTMLButtonElement | null} */ (document.querySelector('[data-cfs-sticky-button]'));
  // Laid over the button's own label (which keeps its live price) while it shows
  const stickyDone = document.createElement('span');
  stickyDone.className = 'cfs-sticky-add__done';
  stickyDone.setAttribute('aria-hidden', 'true'); // the basket message announces the add
  stickyDone.innerHTML = '<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg> Added';
  stickyButton?.append(stickyDone);
  let stickyTimer = 0;
  stickyButton?.addEventListener('click', async (event) => {
    const ok = await add(event);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (ok) {
      stickyButton.dataset.cfsAdded = '';
      await new Promise((resolve) => setTimeout(resolve, 900));
    }
    const stage = document.querySelector('.cfs-product__stage') ?? form;
    const compact = 72; // the compact header bar (64px) and a little air
    window.scrollTo({ top: Math.max(0, stage.getBoundingClientRect().top + window.scrollY - compact), behavior: reduce ? 'auto' : 'smooth' });
    window.clearTimeout(stickyTimer);
    stickyTimer = window.setTimeout(() => {
      delete stickyButton.dataset.cfsAdded;
    }, 1200);
  });

  // Sticky add bar (mobile): visible while the main Add button is out of view, below the fold.
  const sticky = /** @type {HTMLElement | null} */ (document.querySelector('[data-cfs-sticky-add]'));
  const mainActions = form.querySelector('[data-cfs-main-actions]');
  if (sticky && mainActions && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }).observe(mainActions);
  }
}

// Delivery & collection: grey out options this product can't use, from Zapiet's own rules
// (window.ZapietMethods[method].status.product_conditions: tags that exclude a product).
const deliveryBox = /** @type {HTMLElement | null} */ (document.querySelector('[data-cfs-delivery]'));
if (deliveryBox) {
  const tags = (deliveryBox.dataset.productTags ?? '').split('|');
  // Zapiet methods: shipping = UK delivery, delivery = local delivery, pickup = click & collect
  const names = { shipping: 'UK delivery', delivery: 'local delivery', pickup: 'click & collect' };
  const apply = () => {
    const methods = /** @type {any} */ (window).ZapietMethods;
    if (!methods) return false;
    /** @type {string[]} */
    const blocked = [];
    for (const method of Object.keys(names)) {
      const conditions = methods[method]?.status?.product_conditions;
      const excluded = conditions?.enabled && conditions.property === 'tags'
        ? String(conditions.value ?? '').split(',').map((tag) => tag.trim()).some((tag) => tags.includes(tag))
        : false;
      // A method switched off in Zapiet (e.g. local delivery while short-staffed) isn't offered at all
      const enabled = methods[method]?.enabled !== false;
      if (excluded && enabled) blocked.push(method);
      for (const row of deliveryBox.querySelectorAll(`[data-cfs-zapiet-method="${method}"]`)) {
        /** @type {HTMLElement} */ (row).hidden = !enabled;
        row.classList.toggle('is-unavailable', excluded);
      }
    }
    const note = /** @type {HTMLElement | null} */ (deliveryBox.querySelector('[data-cfs-delivery-note]'));
    if (note) {
      note.hidden = blocked.length === 0;
      const offered = Object.keys(names).filter((m) => methods[m]?.enabled !== false && !blocked.includes(m));
      if (blocked.length && offered.length) {
        note.textContent = `This item is ${offered.map((m) => /** @type {any} */ (names)[m]).join(' or ')} only.`;
      } else if (blocked.length) {
        note.textContent = 'This item is not available online at the moment.';
      }
    }
    return true;
  };
  // Zapiet loads after the theme: try now, then a few times while it starts.
  if (!apply()) {
    let tries = 0;
    const timer = window.setInterval(() => {
      if (apply() || ++tries > 20) window.clearInterval(timer);
    }, 500);
  }
}

// "Customers also bought": load recommendations when the row is near the viewport.
const recs = /** @type {HTMLElement | null} */ (document.querySelector('[data-cfs-recs]'));
if (recs?.dataset.url) {
  const load = async () => {
    try {
      const html = await (await fetch(/** @type {string} */ (recs.dataset.url))).text();
      const items = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-cfs-recs-items] > li');
      if (items.length === 0) return;
      recs.querySelector('[data-cfs-recs-track]')?.append(...[...items].map((li) => document.importNode(li, true)));
      recs.hidden = false;
    } catch {
      // Recommendations are optional.
    }
  };
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        load();
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(recs.parentElement ?? recs);
  } else {
    load();
  }
}

// Bundler multibuy (app block): its bundle quantity is a bare number field, so add − / + buttons
// like the main stepper (owner request). They only change the field's value; Bundler still reads
// it on "Add to cart". Bundler re-renders its widget, so new fields are picked up as they appear.
const BUNDLE_QTY = '.cfs-product__app-block .bndlr-add-to-cart-quantity-input';
const STEP_ICONS = {
  '-1': '<path d="M5 12h14"/>',
  '1': '<path d="M12 5v14M5 12h14"/>',
};

/** @param {HTMLInputElement} input @param {number} step */
function stepButton(input, step) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'cfs-bundle-qty__btn';
  button.setAttribute('aria-label', step < 0 ? 'Fewer bundles' : 'More bundles');
  button.innerHTML = `<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round">${STEP_ICONS[String(step)]}</svg>`;
  button.addEventListener('click', () => {
    const min = Number(input.min) || 1;
    const value = Math.min(99, Math.max(min, (parseInt(input.value, 10) || min) + step));
    input.value = String(value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  return button;
}

function enhanceBundleQuantity() {
  for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (document.querySelectorAll(BUNDLE_QTY))) {
    if (input.dataset.cfsStepper) continue;
    input.dataset.cfsStepper = 'true';
    input.setAttribute('aria-label', 'Number of bundles');
    input.inputMode = 'numeric';
    input.closest('.bndlr-floating-label')?.classList.add('cfs-bundle-qty');
    input.before(stepButton(input, -1));
    input.after(stepButton(input, 1));
  }
}

const appBlocks = document.querySelectorAll('.cfs-product__app-block');
if (appBlocks.length) {
  enhanceBundleQuantity();
  const watcher = new MutationObserver(enhanceBundleQuantity);
  for (const block of appBlocks) watcher.observe(block, { childList: true, subtree: true });
}
